import ServiceUnavailableAlert from 'components/core/ServiceUnavailableAlert';
import { useState } from 'react';
import { Alert, Button, Card, Container, Form, InputGroup, Spinner } from 'react-bootstrap';
import { FaArrowRight, FaExclamationTriangle, FaIdCard, FaShieldAlt } from 'react-icons/fa';
import { useHistory } from 'react-router-dom';
import { verifyCpf } from 'services/paciente';
import { isValidCPF, mascaraCPF } from 'utils/helpers';
import PatientRegistrationModal from './PatientRegistrationModal';

export default function CpfVerification({ onSuccess }) {
  const [cpf, setCpf] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [validationError, setValidationError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const history = useHistory();

  const cleanCpf = cpf.replace(/\D/g, '');
  const isCompleteLength = cleanCpf.length === 11;

  const handleCpfChange = (e) => {
    setCpf(mascaraCPF(e.target.value));
    setValidationError('');
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidationError('');
    setError(null);

    if (!isValidCPF(cleanCpf)) {
      setValidationError('O CPF digitado é inválido. Por favor, verifique os números.');
      return;
    }

    setLoading(true);
    try {
      const data = await verifyCpf(cleanCpf);

      if (Array.isArray(data) && data.length > 0) {
        onSuccess?.(data[0]);
      } else {
        setShowModal(true);
      }
    } catch (err) {
      if (err.response?.status === 400 || err.response?.status === 422) {
        setValidationError(
          err.response?.data?.message || 'CPF não encontrado ou formato inválido no servidor.'
        );
      } else {
        setError(err);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleModalClose = () => setShowModal(false);

  const handleSuccess = (patientData) => {
    setShowModal(false);
    if (onSuccess) {
      onSuccess(patientData);
    } else {
      history.push('/paciente/agendamento-online');
    }
  };

  return (
    <Container className="d-flex justify-content-center align-items-center py-4">
      <Card className="w-100" style={{ maxWidth: '460px', borderRadius: '16px' }}>
        <Card.Body className="p-4 text-center">
          <div
            className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
            style={{ width: '60px', height: '60px', backgroundColor: '#eef6ff', color: '#0d6efd' }}
          >
            <FaIdCard size={28} />
          </div>

          <h4 className="fw-bold text-dark mb-1">Agendamento Online</h4>
          <p className="text-muted small mb-4">
            Informe seu CPF para identificarmos seu cadastro na <strong>Clínica Prevencor</strong>.
          </p>

          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3 text-start" controlId="cpfInput">
              <Form.Label className="small fw-semibold text-secondary">Seu CPF</Form.Label>
              <InputGroup size="lg">
                <InputGroup.Text className="bg-light border-end-0 text-muted">
                  <FaIdCard />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  inputMode="numeric"
                  placeholder="000.000.000-00"
                  value={cpf}
                  onChange={handleCpfChange}
                  required
                  isInvalid={Boolean(validationError)}
                  className="border-start-0 bg-light fs-6 fw-semibold"
                  autoFocus
                />
              </InputGroup>
            </Form.Group>

            {/* Alerta de erro de validação de CPF */}
            {validationError && (
              <Alert
                variant="warning"
                className="text-start small py-2 px-3 mb-3 d-flex align-items-center gap-2"
              >
                <FaExclamationTriangle size={16} className="text-warning flex-shrink-0" />
                <span>{validationError}</span>
              </Alert>
            )}

            <Button
              type="submit"
              variant="primary"
              size="lg"
              className="w-100 fw-bold rounded-3 d-flex align-items-center justify-content-center gap-2 py-2"
              disabled={loading || !isCompleteLength}
            >
              {loading ? (
                <>
                  <Spinner animation="border" size="sm" />
                  <span>Verificando...</span>
                </>
              ) : (
                <>
                  <span>Continuar Agendamento</span>
                  <FaArrowRight size={14} />
                </>
              )}
            </Button>
          </Form>

          {/* Erro de conexão / Infraestrutura */}
          {error && (
            <div className="mt-3 text-start">
              <ServiceUnavailableAlert error={error} stale={false} onRetry={() => setError(null)} />
            </div>
          )}

          <div className="mt-4 pt-3 border-top d-flex align-items-center justify-content-center gap-2 text-muted small">
            <FaShieldAlt className="text-success" size={14} />
            <span style={{ fontSize: '0.78rem' }}>
              Seus dados estão protegidos e serão usados apenas para a consulta.
            </span>
          </div>
        </Card.Body>
      </Card>

      {showModal && (
        <PatientRegistrationModal
          show={showModal}
          onHide={handleModalClose}
          cpf={cleanCpf}
          onSuccess={handleSuccess}
        />
      )}
    </Container>
  );
}
